import React from 'react';
import { BookmarkModal, HistoricDocumentType } from '~/components/Patient';
import { useSortable, formatDate, useRoleActions } from '~/util';
import { PatientContext } from '~/services';

export interface HistoricDocumentResultsProps {
    show: boolean;
    results: HistoricDocumentType[];
    error: boolean;
}

export function HistoricDocumentResults({ show, results, error }: HistoricDocumentResultsProps) {
    const patientContext = React.useContext(PatientContext);
    const canViewDocs = useRoleActions('allow_open_bookmark_documents', null);
    const { sort, showSortHeader } = useSortable('Date', 'DESC');
    const [openDoc, setOpenDoc] = React.useState('');

    function sortResults(a: HistoricDocumentType, b: HistoricDocumentType) {
        if (sort.field === 'Date') {
            const atime = new Date(a.bookmarkDocumentDate).getTime();
            const btime = new Date(b.bookmarkDocumentDate).getTime();
            return sort.dir === 'ASC' ? atime - btime : btime - atime;
        }
        if (sort.field === 'Name') {
            return sort.dir === 'ASC'
                ? a.bookmarkDocumentName.localeCompare(b.bookmarkDocumentName)
                : b.bookmarkDocumentName.localeCompare(a.bookmarkDocumentName);
        }
        return sort.dir === 'ASC'
            ? a.bookmarkDocumentType.localeCompare(b.bookmarkDocumentType)
            : b.bookmarkDocumentType.localeCompare(a.bookmarkDocumentType);
    }

    if (!show) return null;
    if (error) {
        return (
            <div className="mt-3 mx-5 text-center">
                <p>Apologies, the requested data could not be retrieved at this time.</p>
                <p>
                    Please try again later. If the issue persists, please contact the support team
                    at <a href="mailto:HelpDesk@wellmed.net">HelpDesk@wellmed.net</a> or
                    1-877-435-7576.
                </p>
            </div>
        );
    }
    if (results.length === 0) {
        return <div className="text-center h5">There are no results for your current search</div>;
    }

    return (
        <div className="table-scroll bookmark-documents-table-wrapper">
            <table className="table table-striped table-bordered fixed-table bookmark-documents-table">
                <thead className="table-dark">
                    <tr>
                        {showSortHeader('Name')}
                        {showSortHeader('Type')}
                        {showSortHeader('Date', '', true)}
                    </tr>
                </thead>
                <tbody>
                    {results.sort(sortResults).map((doc) => (
                        <tr key={doc.bookmarkFileDataId}>
                            <td className="text-truncate" title={doc.bookmarkDocumentName}>
                                {canViewDocs ? (
                                    <>
                                        <button
                                            type="button"
                                            className="link-button text-truncate"
                                            onClick={() => setOpenDoc(doc.bookmarkFileDataId)}
                                        >
                                            {doc.bookmarkDocumentName}
                                        </button>
                                        {openDoc === doc.bookmarkFileDataId && (
                                            <BookmarkModal
                                                handleClose={() => setOpenDoc('')}
                                                patientGmpi={patientContext?.patientGmpi || null}
                                                fileId={doc.bookmarkFileDataId}
                                                isHistoricDoc
                                            />
                                        )}
                                    </>
                                ) : (
                                    doc.bookmarkDocumentName
                                )}
                            </td>
                            <td>{doc.bookmarkDocumentType}</td>
                            <td>{formatDate(doc.bookmarkDocumentDate)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
